import React, { useState } from 'react'
import { DOCUMENTATION_SLOT_DEFINITIONS } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import type { RubroRow } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import type { DocumentationSlotKey } from '../../../../../utils/conversationalMode/documentationSlotSummaryUtils.ts'
import {
  applyMetricRemovalWithBudgetCascade,
  captureMetricRemovalRestorePackage,
  shouldWarnBeforeMetricRemoval
} from '../../../../../utils/conversationalMode/rubroMetricBudgetCoherenceUtils.ts'
import { rubroMetricReadyFromRow } from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import {
  captureRubroSlotAssignment,
  clearRubroSlotAssignment,
  restoreRubroSlotAssignment
} from '../../../../../utils/conversationalMode/rubroSlotDeliverableUtils.ts'
import { restoreMetricRemovalPackage } from '../../../../../utils/conversationalMode/rubroMetricBudgetCoherenceUtils.ts'
import RubroDocumentationBlock from './RubroDocumentationBlock.tsx'
import type { RubroSlotOriginHandlers } from './RubroSlotOriginHandlers.ts'
import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'
import RubroMetricRemoveWarnModal from './RubroMetricRemoveWarnModal.tsx'
import styles from './BudgetSheetCompactEntry.module.scss'

type Props = RubroSlotOriginHandlers & {
  slot: DocumentationSlotKey
  showRemove: boolean
  showPrimary?: boolean
  primaryLabel?: string
  onPrimary?: () => void
  primaryDisabled?: boolean
  meta?: string | null
  pdfHref?: string | null
  originPickerOpen: boolean
  onOriginPickerOpenChange: (open: boolean) => void
  removedSnapshot: Partial<Omit<RubroRow, 'tempId'>> | null
  onRemovedSnapshotChange: (snapshot: Partial<Omit<RubroRow, 'tempId'>> | null) => void
}

const RubroDeliverableSlotActions: React.FC<Props> = ({
  slot,
  showRemove,
  showPrimary = false,
  primaryLabel = 'Ver y editar',
  onPrimary,
  primaryDisabled = false,
  meta,
  pdfHref,
  originPickerOpen,
  onOriginPickerOpenChange,
  removedSnapshot,
  onRemovedSnapshotChange,
  tempId,
  rubro,
  draftId,
  draftLocked,
  isSaving,
  updateRubro,
  docPdfUrl,
  docPageCount,
  metadataFiles,
  slots,
  fileIds,
  documentationSlotSources,
  uploadingSlotKey,
  onSelectSlotFileId,
  onSlotPagesChange,
  onRemoveSlotPageChip,
  onUploadSlotFile
}) => {
  const canAct = !!draftId && !isSaving && !draftLocked
  const label =
    DOCUMENTATION_SLOT_DEFINITIONS.find((d) => d.key === slot)?.label.toLowerCase() ?? slot
  const [metricRemoveWarnOpen, setMetricRemoveWarnOpen] = useState(false)

  if (!canAct) return null

  const performRemove = () => {
    if (slot === 'metricComputation') {
      const snapshot = captureMetricRemovalRestorePackage(rubro)
      if (snapshot) onRemovedSnapshotChange(snapshot)
      updateRubro(tempId, applyMetricRemovalWithBudgetCascade(rubro))
    } else {
      const snapshot = captureRubroSlotAssignment(rubro, slot)
      if (snapshot) onRemovedSnapshotChange(snapshot)
      updateRubro(tempId, clearRubroSlotAssignment(rubro, slot))
    }
    onOriginPickerOpenChange(true)
  }

  const handleRemoveClick = () => {
    if (slot === 'metricComputation' && shouldWarnBeforeMetricRemoval(rubro)) {
      setMetricRemoveWarnOpen(true)
      return
    }
    performRemove()
  }

  const handleRemoveWarnConfirm = () => {
    setMetricRemoveWarnOpen(false)
    performRemove()
  }

  const handleRestore = () => {
    if (!removedSnapshot) return
    const restorePatch =
      slot === 'metricComputation'
        ? restoreMetricRemovalPackage(rubro, removedSnapshot)
        : restoreRubroSlotAssignment(rubro, removedSnapshot)
    updateRubro(tempId, restorePatch)
    onRemovedSnapshotChange(null)
    onOriginPickerOpenChange(false)
  }

  const handlePickerDone = () => {
    if (slot === 'metricComputation') {
      if (rubroMetricReadyFromRow(rubro)) onRemovedSnapshotChange(null)
    } else {
      onRemovedSnapshotChange(null)
    }
    onOriginPickerOpenChange(false)
  }

  const metricRemovalHadGeneratedBudget =
    slot === 'metricComputation' &&
    !!removedSnapshot &&
    'budgetSheetState' in removedSnapshot &&
    !!removedSnapshot.budgetSheetState

  const showSlotActionBar =
    !originPickerOpen &&
    (!!removedSnapshot ||
      (showPrimary && onPrimary) ||
      !!pdfHref ||
      showRemove ||
      !!meta)

  const restoreLabel =
    slot === 'metricComputation' && removedSnapshot && 'budgetSheetState' in removedSnapshot
      ? 'Restaurar cómputo y planilla'
      : 'Restaurar lo que quitamos'

  return (
    <>
      {showSlotActionBar ? (
        <div className={styles.sheetActionRow}>
          {meta ? <p className={styles.sheetMeta}>{meta}</p> : <span className={styles.sheetActionRowSpacer} aria-hidden />}
          <div className={styles.sheetActionGroup}>
            {removedSnapshot ? (
              <button type="button" className={styles.restoreInline} disabled={isSaving} onClick={handleRestore}>
                {restoreLabel}
              </button>
            ) : null}
            {removedSnapshot && slot === 'metricComputation' ? (
              <button
                type="button"
                className={styles.primaryInline}
                disabled={isSaving}
                onClick={() => onOriginPickerOpenChange(true)}
              >
                Cargar otro cómputo
              </button>
            ) : null}
            {pdfHref ? (
              <a href={pdfHref} target="_blank" rel="noopener noreferrer" className={styles.primaryInline}>
                Ver PDF
              </a>
            ) : null}
            {showPrimary && onPrimary ? (
              <button
                type="button"
                className={styles.primaryInline}
                disabled={primaryDisabled}
                onClick={onPrimary}
              >
                {primaryLabel}
              </button>
            ) : null}
            {showRemove ? (
              <button type="button" className={styles.removeInline} disabled={isSaving} onClick={handleRemoveClick}>
                Quitar
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {originPickerOpen ? (
        <div className={styles.originPickerWrap}>
          {metricRemovalHadGeneratedBudget ? (
            <ProjectAutofillInfoNote tone="caution" className={styles.originPickerLead}>
              Sin cómputo métrico no se adjuntará la planilla de cotización generada. Cargá tu cómputo abajo o
              restaurá el original; para una planilla nueva acorde a tu cómputo, incluilo en la documentación y
              re-analizá el proyecto.
            </ProjectAutofillInfoNote>
          ) : null}
          <ProjectAutofillInfoNote variant="rubroInfo" className={styles.originPickerLead}>
            Elegí qué páginas o archivo enviar como {label}.
          </ProjectAutofillInfoNote>
          <div className={styles.originPickerActions}>
            {removedSnapshot ? (
              <button type="button" className={styles.restoreInline} disabled={isSaving} onClick={handleRestore}>
                {slot === 'metricComputation' && 'budgetSheetState' in removedSnapshot
                  ? 'Restaurar cómputo y planilla'
                  : 'Restaurar lo que quitamos'}
              </button>
            ) : null}
            <button type="button" className={styles.originPickerDone} disabled={isSaving} onClick={handlePickerDone}>
              Listo
            </button>
          </div>
          <RubroDocumentationBlock
            correctionMode
            correctionSlot={slot}
            tempId={tempId}
            slots={slots}
            pdfBaseUrl={docPdfUrl}
            maxPage={docPageCount ?? undefined}
            fileIds={fileIds}
            draftFiles={metadataFiles}
            disabled={!draftId || isSaving || draftLocked}
            uploadingSlotKey={uploadingSlotKey}
            onSelectSlotFileId={onSelectSlotFileId}
            onSlotPagesChange={onSlotPagesChange}
            onRemoveSlotPageChip={onRemoveSlotPageChip}
            documentationSlotSources={documentationSlotSources}
            onUploadSlotFile={onUploadSlotFile}
          />
        </div>
      ) : null}
      {slot === 'metricComputation' ? (
        <RubroMetricRemoveWarnModal
          open={metricRemoveWarnOpen}
          onCancel={() => setMetricRemoveWarnOpen(false)}
          onConfirm={handleRemoveWarnConfirm}
          busy={isSaving}
        />
      ) : null}
    </>
  )
}

export default RubroDeliverableSlotActions
